import React,{forwardRef,useImperativeHandle,useLayoutEffect,useRef} from 'react';
import {flushSync} from 'react-dom';
import {createDrawingTools,drawingCSS} from '../../components/editors/drawingTools';
import {materialInk as core} from './materialAnnotationModel';

// The unscaled scroller contains a fixed-size, position:relative page (stage).
// Apply CSS zoom to that entire page, including its content and this SVG overlay.
// Keep the toolbar outside the page. Coordinates are saved in base-page pixels.
const signature=value=>JSON.stringify(core.normalize(value));

const MaterialInkLayer=forwardRef(function MaterialInkLayer({drawings,onChange,pageKey,zoom=1,stageRef,scrollerRef,toolbarRef,readOnly=false,active=true},ref){
    const latest=useRef();latest.current={drawings,onChange,pageKey,zoom,readOnly};
    const instance=useRef(null),lastSignature=useRef(null),compact=useRef(false);
    useImperativeHandle(ref,()=>({
        flush(){instance.current?.flush();return instance.current?.getData() ?? null;},
        getDrawings(){return instance.current?.getData() ?? null;},
        refresh(){instance.current?.refresh();}
    }),[]);

    useLayoutEffect(()=>{
        const stage=stageRef.current,scroller=scrollerRef.current,toolbar=toolbarRef.current;
        if(!active || !stage || !scroller || !toolbar)return;
        const key=pageKey,doc=stage.ownerDocument,pageOnChange=latest.current.onChange;
        let controller;
        controller=createDrawingTools({stage,scroller,toolbar,pageSurface:true,getScale:()=>Math.max(.25,Math.min(2,Number(latest.current.zoom)||1)),onChange:()=>{
            const next=controller.getData(),json=signature(next);
            if(json===lastSignature.current)return;
            lastSignature.current=json;
            const notify=latest.current.pageKey===key?latest.current.onChange:pageOnChange;
            notify?.(next,key);
        }},core);
        instance.current=controller;
        controller.load(latest.current.drawings,latest.current.readOnly);
        lastSignature.current=signature(controller.getData());
        if(compact.current)toolbar.querySelector('[data-action="collapse"]')?.click();
        // Finish a pen stroke before a page/navigation button reads the notes.
        const beforeAction=event=>{
            if(!stage.contains(event.target))flushSync(()=>controller.flush());
        };
        const onVisibility=()=>{if(doc.hidden)controller.flush();};
        doc.addEventListener('pointerdown',beforeAction,true);
        doc.addEventListener('click',beforeAction,true);
        doc.addEventListener('visibilitychange',onVisibility);
        return()=>{
            compact.current=toolbar.classList.contains('ink-compact');
            controller.flush();
            doc.removeEventListener('pointerdown',beforeAction,true);
            doc.removeEventListener('click',beforeAction,true);
            doc.removeEventListener('visibilitychange',onVisibility);
            controller.destroy();
            if(instance.current===controller)instance.current=null;
        };
    },[active,pageKey,stageRef,scrollerRef,toolbarRef]);

    useLayoutEffect(()=>{
        const controller=instance.current;if(!controller)return;
        const incoming=signature(drawings);
        // Own autosave echoes must not reset the current tool, history or glow.
        if(incoming!==lastSignature.current){controller.flush();controller.load(drawings,latest.current.readOnly);lastSignature.current=signature(controller.getData());}
    },[drawings]); // readOnly changes are handled independently below.
    const wasReadOnly=useRef(readOnly);
    useLayoutEffect(()=>{
        if(wasReadOnly.current!==readOnly && instance.current){instance.current.flush();instance.current.load(instance.current.getData(),readOnly);}
        wasReadOnly.current=readOnly;
    },[readOnly]);
    useLayoutEffect(()=>{instance.current?.flush();instance.current?.refresh();},[zoom]);
    return <style>{drawingCSS}</style>;
});

export default MaterialInkLayer;
